/**
 * The design's Export menu, split so the part that can be tested is tested.
 *
 * Design rule 4 requires every grid to offer CSV, Excel and PDF, and this console has three grids.
 * The dispatch and tenant admin consoles each wrote the whole of it inside the component that uses
 * it, which was right while there was one grid per file and is three copies here — so the two
 * decisions that are decidable without a browser are in this module and tested in
 * `tests/export.test.ts`, and the two that need one are the last two functions.
 *
 * **Why Excel is an HTML table.** `application/vnd.ms-excel` with an HTML body is what every
 * browser-side "export to Excel" has meant in practice for twenty years: Excel parses it, and
 * writing a real XLSX would mean a zip writer in the bundle for a feature whose alternative is CSV.
 * The design's own `gridUI()` does exactly this.
 *
 * **Why PDF is the browser's print dialogue.** Same reason, and the design does the same: it opens
 * a window, writes a table and calls `print()`. A PDF writer would be a second rendering engine.
 *
 * What this module deliberately does **not** do is send anything anywhere. The design's `Export
 * entries` form promises that "exports are recorded in the audit log"; nothing on this platform
 * records one, and recording it would need `audit:write` — the permission to append to the log —
 * which no board here asks for. R-35, and the same finding R-12 made on the audit board itself.
 */

/** The formats the design's menu offers, in its order and its words. */
export const EXPORT_FORMATS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

export type ExportFormat = (typeof EXPORT_FORMATS)[number]["format"];

/** One rectangle of text: a header row and the body rows, already in the words on screen. */
export interface Sheet {
  readonly header: readonly string[];
  readonly rows: readonly (readonly string[])[];
}

/**
 * RFC 4180 comma-separated values: every field quoted, every embedded quote doubled.
 *
 * Every field, not only the ones that need it, and that is the decision worth stating. This console
 * exports money, written `KES 1,184,000.00` — a field with a comma in it, on every row of every
 * board. An exporter that quoted only when it detected a delimiter would be correct and would be
 * one mis-detection away from splitting an amount across two columns, which is a spreadsheet of
 * wrong numbers that opens without complaint. Quoting unconditionally cannot have that bug.
 *
 * `\r\n` as the line terminator, which is what RFC 4180 specifies and what Excel's own importer
 * assumes; a bare `\n` is tolerated by most spreadsheets and not by all of them.
 */
export function toCsv(sheet: Sheet): string {
  const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
  return [sheet.header, ...sheet.rows].map((line) => line.map(quote).join(",")).join("\r\n");
}

/** `&`, `<` and `>` escaped, so a reason code containing a bracket cannot close a cell. */
export function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

/**
 * The same rectangle as an HTML table, which is both the Excel body and the printable one.
 *
 * The design's own markup, value for value: a 1px collapsed border, 6px cell padding, 12px
 * sans-serif.
 */
export function toHtmlTable(sheet: Sheet): string {
  const head = sheet.header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("");
  const body = sheet.rows
    .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");
  return (
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${head}</tr>${body}</table>`
  );
}

/** Hand the browser a file. A link rather than an API, because that is what works everywhere. */
function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

/**
 * Write what the agent is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * enum — so a row read out of a file months later says `completed` and `KES 30.00`, which is what
 * the agent saw, rather than `SUCCEEDED` and `3000`.
 *
 * @param name the file's stem, which is also the printable document's heading.
 */
export function exportSheet(sheet: Sheet, format: ExportFormat, name: string): void {
  if (format === "csv") {
    download(new Blob([toCsv(sheet)], { type: "text/csv" }), `${name}.csv`);
    return;
  }

  const table = toHtmlTable(sheet);

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      `${name}.xls`,
    );
    return;
  }

  const printable = window.open("", "_blank");
  // A blocked popup is not an error worth a dialogue: the agent pressed a menu item and nothing
  // happened, which their own browser told them about.
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>${escapeHtml(name)}</title></head><body>` +
      `<h2 style="font-family:sans-serif">${escapeHtml(name)}</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}
